// A shared kitchen tablet (backend/kitchen_devices.py, G-AUTH-2). A manager registers this browser for one site; the
// device token is kept here, like the session token, and sent as X-Kitchen-Device. The server keeps only its hash.
// On a registered browser a member of the site signs in with their PIN, and the idle lock returns to the PIN screen.

export const DEVICE_TOKEN_KEY = 'kitchen_device_token';
export const DEVICE_INFO_KEY = 'kitchen_device';
export const DEVICE_HEADER = 'X-Kitchen-Device';
export const DEFAULT_DEVICE_IDLE_MINUTES = 2;   // kitchen_devices.DEFAULT_IDLE_LOCK_MINUTES

const read = (key) => { try { return localStorage.getItem(key); } catch { return null; } };

export function readDeviceToken() {
  return read(DEVICE_TOKEN_KEY) || '';
}

export function readDeviceInfo() {
  try { return JSON.parse(read(DEVICE_INFO_KEY) || 'null'); } catch { return null; }
}

export function rememberDevice(token, device) {
  try {
    if (token) localStorage.setItem(DEVICE_TOKEN_KEY, token);
    if (device) localStorage.setItem(DEVICE_INFO_KEY, JSON.stringify(device));
  } catch { /* storage blocked: the device simply is not registered here */ }
}

export function forgetDevice() {
  try { localStorage.removeItem(DEVICE_TOKEN_KEY); localStorage.removeItem(DEVICE_INFO_KEY); } catch { /* ignore */ }
}

/** The idle lock for this browser when it is a kitchen device (minutes), else null. */
export function deviceIdleMinutes() {
  if (!readDeviceToken()) return null;
  const n = Number(readDeviceInfo()?.idle_lock_minutes);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_DEVICE_IDLE_MINUTES;
}

const detailOf = (body) => (body && typeof body.detail === 'object' && body.detail) || { message: body?.detail };

/** GET /api/auth/kitchen-device: the device, its site and the members with a PIN. A 401 forgets the device here. */
export async function loadKitchenDevice(apiUrl) {
  const token = readDeviceToken();
  if (!token) return { ok: false, reason: 'unregistered' };
  try {
    const res = await fetch(`${apiUrl || ''}/api/auth/kitchen-device`, { headers: { [DEVICE_HEADER]: token } });
    const body = await res.json().catch(() => ({}));
    if (res.ok) { rememberDevice(null, body.device); return { ok: true, data: body }; }
    if (res.status === 401) { forgetDevice(); return { ok: false, reason: 'unregistered', message: detailOf(body).message }; }
    return { ok: false, reason: 'error', message: detailOf(body).message };
  } catch {
    return { ok: false, reason: 'error', message: 'Could not reach the server. Check the connection.' };
  }
}

/** POST /api/auth/pin-login. `data` on success is what the password sign-in returns. */
export async function pinSignIn(apiUrl, userId, pin) {
  let res;
  try {
    res = await fetch(`${apiUrl || ''}/api/auth/pin-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', [DEVICE_HEADER]: readDeviceToken() },
      body: JSON.stringify({ user_id: userId, pin }),
    });
  } catch {
    return { ok: false, reason: 'error', message: 'Could not reach the server. Check the connection.' };
  }
  const body = await res.json().catch(() => ({}));
  if (res.ok) return { ok: true, data: body };
  const detail = detailOf(body);
  if (res.status === 401 && detail.code === 'wrong_pin') {
    return { ok: false, reason: 'wrong', triesLeft: detail.tries_left,
      message: detail.tries_left > 0 ? `That PIN is not right. ${detail.tries_left} tries left.`
        : 'That PIN is not right. You are locked out on this device.' };
  }
  if (res.status === 401) { forgetDevice(); return { ok: false, reason: 'unregistered', message: detail.message }; }
  if (res.status === 423) return { ok: false, reason: 'locked', message: detail.message };
  if (res.status === 422) return { ok: false, reason: 'wrong', message: 'A PIN is 4 to 6 digits.' };
  return { ok: false, reason: 'refused', message: detail.message || 'You cannot sign in with a PIN here.' };
}
